import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { signMessage } from "@/helper/contractFunctions";

import {
  approveTicketContract,
  boughtTicket,
  createProfile,
  userTicketId,
} from "@/helper/contractFunctions/MafiaProfile";

import { Errors } from "@/constants/errors";

import { ProfileNames } from "@/types/ProfileName";
import { UserProfile } from "@/types/UserProfile";

import { delay, toastError } from "@/utils/utils";
import { SERVER_URL } from "@/config/config";

const MY_PROFILE_CITY_RETRY_COUNT = 3;
const MY_PROFILE_CITY_RETRY_DELAY_MS = 1000;

const isValidCityId = (city: unknown): boolean => {
  const value = Number(city);
  return Number.isInteger(value) && value >= 0;
};

const resolveProfileCity = (
  incomingCity: unknown,
  previousCity: unknown
): number | undefined => {
  if (isValidCityId(incomingCity)) return Number(incomingCity);
  if (isValidCityId(previousCity)) return Number(previousCity);
  return undefined;
};

export interface ProfileState {
  profile: UserProfile;
  isLoadingProfile: boolean;

  myProfile: UserProfile;
  isLoadingMyProfile: boolean;

  latestProfiles: UserProfile[];
  isLoadingLatestProfiles: boolean;

  profileNames: ProfileNames[];
  isLoadingProfileNames: boolean;
  profileNamesStatus: "idle" | "loading" | "succeeded" | "failed";

  isCreatingProfile: boolean;

  isSigningProfileData: boolean;
  isUpdatingProfileDescription: boolean;
  isUpdatingProfileDisplaySetting: boolean;

  isUpdatingAvatar: boolean;
  isUpdatingBackground: boolean;
}

const initialState: ProfileState = {
  profile: {},
  isLoadingProfile: false,

  myProfile: {},
  isLoadingMyProfile: false,
  latestProfiles: [],
  isLoadingLatestProfiles: false,

  profileNames: [],
  isLoadingProfileNames: false,
  profileNamesStatus: "idle",

  isCreatingProfile: false,

  isSigningProfileData: false,
  isUpdatingProfileDescription: false,
  isUpdatingProfileDisplaySetting: false,

  isUpdatingAvatar: false,
  isUpdatingBackground: false,

};

export const registerProfile = createAsyncThunk(
  "profile/registerProfile",
  async ({ signature, signMsg }: { signature: string; signMsg: any }, { dispatch }) => {
    const response = await axios.put(`${SERVER_URL}/profile`, {
      signature,
      signMsg,
    });

    await dispatch(getProfileNames());
    return response.data.name;
  }
);

export const getProfile = createAsyncThunk(
  "profile/getProfile",
  async (name: string) => {
    const response = await axios.get(`${SERVER_URL}/profile/name/${name}`);

    return response.data;
  }
);

export const getMyProfile = createAsyncThunk(
  "profile/getMyProfile",
  async (name: string) => {
    let data: any;

    for (let attempt = 0; attempt < MY_PROFILE_CITY_RETRY_COUNT; attempt++) {
      const response = await axios.get(`${SERVER_URL}/profile/name/${name}`);
      data = response.data;

      // Backend can briefly return city = -1 (indexer lag after travel / profile create).
      if (isValidCityId(data?.city)) {
        return data;
      }

      if (attempt < MY_PROFILE_CITY_RETRY_COUNT - 1) {
        await delay(MY_PROFILE_CITY_RETRY_DELAY_MS);
      }
    }

    return data;
  }
);

export const getLatestProfiles = createAsyncThunk(
  "profile/getLatestProfiles",
  async () => {
    const response = await axios.get(`${SERVER_URL}/profile/latest`);

    return response.data;
  }
);

export const getProfileNames = createAsyncThunk(
  "profile/getProfileNames",
  async () => {
    const response = await axios.get(`${SERVER_URL}/profile/names`);

    return response.data;
  }
);

export const signData = createAsyncThunk(
  "profile/signData",
  async ({ signMsg }: { signMsg: any }) => {
    const signature = await signMessage(signMsg);

    return signature;
  }
);

export const updateProfileDescription = createAsyncThunk(
  "profile/updateProfileDescription",
  async ({
    userId,
    signMsg,
    signature,
  }: {
    userId: number;
    signMsg: any;
    signature: string;
  }) => {
    const response = await axios.put(
      `${SERVER_URL}/profile/description/${userId}`,
      {
        signMsg,
        signature,
      }
    );

    return response.data;
  }
);

export const updateProfileDisplaySetting = createAsyncThunk(
  "profile/updateProfileDisplaySetting",
  async ({
    userId,
    signMsg,
    signature,
  }: {
    userId: number;
    signMsg: any;
    signature: string;
  }) => {
    const response = await axios.put(
      `${SERVER_URL}/profile/inventoryDisplay/${userId}`,
      {
        signMsg,
        signature,
      }
    );

    return response.data;
  }
);

export const approveProfileTicketAction = createAsyncThunk(
  "profile/approveProfileTicketAction",
  async (account: string) => {
    await approveTicketContract(account);
  }
);

export const createProfileAction = createAsyncThunk(
  "profile/createProfileAction",
  async ({ name, account, referrer, gender, country, avatarNumber }: { name: string; account: string; referrer: string; gender: number; country: string; avatarNumber: number }) => {
    await createProfile(name, account, referrer, gender, country, avatarNumber);
  }
);

export const getUserTicketId = createAsyncThunk(
  "profile/getUserTicketId",
  async (account: string) => {
    const data = await userTicketId(account);

    return Number(data);
  }
);

export const getUserBoughtTicket = createAsyncThunk(
  "profile/getUserBoughtTicket",
  async (account: string) => {
    const data: boolean = await boughtTicket(account);

    return data;
  }
);

export const signProfile = createAsyncThunk(
  "profile/signProfile",
  async ({ signMsg }: { signMsg: any }) => {
    const signature = await signMessage(signMsg);

    return signature;
  }
);

export const uploadAvatarAction = createAsyncThunk(
  "profile/uploadAvatarAction",
  async ({ avatarUrl }: { avatarUrl: string }) => {
    const data = await axios.put(`${SERVER_URL}/profile/custom/avatar`, {
      avatarUrl,
    });

    return data.data;
  }
);

export const uploadBackgroundAction = createAsyncThunk(
  "profile/uploadBackgroundAction",
  async ({ backgroundUrl }: { backgroundUrl: string }) => {
    const data = await axios.put(`${SERVER_URL}/profile/custom/background`, {
      backgroundUrl,
    });

    return data.data;
  }
);

export const deleteAvatarAction = createAsyncThunk(
  "profile/deleteAvatarAction",
  async () => {
    const data = await axios.delete(`${SERVER_URL}/profile/custom/avatar`);
    return data.data;
  }
);

export const deleteBackgroundAction = createAsyncThunk(
  "profile/deleteBackgroundAction",
  async () => {
    const data = await axios.delete(`${SERVER_URL}/profile/custom/background`);
    return data.data;
  }
);

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setIsCreating: (state, { payload }) => {
      state.isCreatingProfile = payload;
    },

    clearMyProfile: (state) => {
      state.myProfile = {};
    },
    setIsUpdatingAvatar: (state, { payload }) => {
      state.isUpdatingAvatar = payload;
    },
    setIsUpdatingBackground: (state, { payload }) => {
      state.isUpdatingBackground = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(approveProfileTicketAction.pending, (state) => {
      state.isCreatingProfile = true;
    });
    builder.addCase(
      approveProfileTicketAction.fulfilled,
      (state, { payload }) => { }
    );
    builder.addCase(approveProfileTicketAction.rejected, (state, { error }) => {
      state.isCreatingProfile = false;
    });

    builder.addCase(createProfileAction.pending, (state) => {
      state.isCreatingProfile = true;
    });
    builder.addCase(createProfileAction.fulfilled, (state, { payload }) => { });
    builder.addCase(createProfileAction.rejected, (state, { error }) => {
      state.isCreatingProfile = false;
    });

    builder.addCase(signProfile.pending, (state) => {
      state.isCreatingProfile = true;
    });
    builder.addCase(signProfile.fulfilled, (state, { payload }) => {
      state.isCreatingProfile = false;
    });
    builder.addCase(signProfile.rejected, (state, { error }) => {
      state.isCreatingProfile = false;
    });

    builder.addCase(registerProfile.pending, (state) => { });
    builder.addCase(registerProfile.fulfilled, (state, { payload }) => {
      state.isCreatingProfile = false;
    });
    builder.addCase(registerProfile.rejected, (state, { error }) => {
      state.isCreatingProfile = false;
    });

    builder.addCase(getProfile.pending, (state) => {
      state.isLoadingProfile = true;
    });
    builder.addCase(getProfile.fulfilled, (state, { payload }) => {
      state.isLoadingProfile = false;
      state.profile = {
        ...payload,
        createdDate: payload.date_created,
        inventoryVisibility: payload.inventory_display,
        premiumExpire: payload.premiumExpire ?? payload.premium_expire,
        premiumStart: payload.premiumStart ?? payload.premium_start,
      } as UserProfile;
    });
    builder.addCase(getProfile.rejected, (state, { error }) => {
      state.isLoadingProfile = false;
      toastError(Errors.GLOBAL.PROFILE.GETTING_FAILED);
    });

    builder.addCase(getMyProfile.pending, (state) => {
      state.isLoadingMyProfile = true;
    });
    builder.addCase(getMyProfile.fulfilled, (state, { payload }) => {
      state.isLoadingMyProfile = false;
      state.myProfile = {
        ...payload,
        inventoryVisibility: payload.inventory_display,
        createdDate: payload.date_created,
        safehouseUntil: payload.safehouseUntil ?? payload.safeUntil ?? 0,
        nextSafehouseEnterTime:
          payload.nextSafehouseEnterTime ?? payload.nextSafehouseTime ?? 0,
        premiumExpire: payload.premiumExpire ?? payload.premium_expire,
        premiumStart: payload.premiumStart ?? payload.premium_start,
        city: resolveProfileCity(payload?.city, state.myProfile.city),
      } as UserProfile;
    });
    builder.addCase(getMyProfile.rejected, (state, { error }) => {
      state.isLoadingMyProfile = false;
      toastError(Errors.GLOBAL.PROFILE.GETTING_FAILED);
    });

    builder.addCase(getLatestProfiles.pending, (state) => {
      state.isLoadingLatestProfiles = true;
    });
    builder.addCase(getLatestProfiles.fulfilled, (state, { payload }) => {
      state.isLoadingLatestProfiles = false;
      state.latestProfiles = payload;
    });
    builder.addCase(getLatestProfiles.rejected, (state, { error }) => {
      state.isLoadingLatestProfiles = false;
    });

    builder.addCase(getProfileNames.pending, (state) => {
      state.isLoadingProfileNames = true;
      state.profileNamesStatus = "loading";
    });
    builder.addCase(getProfileNames.fulfilled, (state, { payload }) => {
      state.profileNames = payload;
      state.isLoadingProfileNames = false;
      state.profileNamesStatus = "succeeded";
    });
    builder.addCase(getProfileNames.rejected, (state, { error }) => {
      state.isLoadingProfileNames = false;
      state.profileNamesStatus = "failed";
    });

    builder.addCase(updateProfileDescription.pending, (state) => {
      state.isUpdatingProfileDescription = true;
    });
    builder.addCase(
      updateProfileDescription.fulfilled,
      (state, { payload }) => {
        state.isUpdatingProfileDescription = false;
      }
    );
    builder.addCase(updateProfileDescription.rejected, (state, { error }) => {
      state.isUpdatingProfileDescription = false;
    });

    builder.addCase(updateProfileDisplaySetting.pending, (state) => {
      state.isUpdatingProfileDisplaySetting = true;
    });
    builder.addCase(
      updateProfileDisplaySetting.fulfilled,
      (state, { payload }) => {
        state.isUpdatingProfileDisplaySetting = false;
      }
    );
    builder.addCase(
      updateProfileDisplaySetting.rejected,
      (state, { error }) => {
        state.isUpdatingProfileDisplaySetting = false;
      }
    );

    builder.addCase(signData.pending, (state) => {
      state.isSigningProfileData = true;
    });
    builder.addCase(signData.fulfilled, (state, { payload }) => {
      state.isSigningProfileData = false;
    });
    builder.addCase(signData.rejected, (state, { error }) => {
      state.isSigningProfileData = false;
    });

    builder.addCase(uploadAvatarAction.pending, (state) => {
      state.isUpdatingAvatar = true;
    });
    builder.addCase(
      uploadAvatarAction.fulfilled,
      (state, { payload, meta }) => {
        state.isUpdatingAvatar = false;

        state.myProfile.customAvatar = meta.arg.avatarUrl;
      }
    );
    builder.addCase(uploadAvatarAction.rejected, (state, { error }) => {
      state.isUpdatingAvatar = false;
      console.error(error);
    });

    builder.addCase(uploadBackgroundAction.pending, (state) => {
      state.isUpdatingBackground = true;
    });
    builder.addCase(uploadBackgroundAction.fulfilled, (state, { meta }) => {
      state.isUpdatingBackground = false;

      state.myProfile.customBackground = meta.arg.backgroundUrl;
    });
    builder.addCase(uploadBackgroundAction.rejected, (state, { error }) => {
      state.isUpdatingBackground = false;
      console.error(error);
    });

    builder.addCase(deleteAvatarAction.pending, (state) => {
      state.isUpdatingAvatar = true;
    });
    builder.addCase(deleteAvatarAction.fulfilled, (state, { payload }) => {
      state.isUpdatingAvatar = false;
      state.myProfile.customAvatar = "";
    });
    builder.addCase(deleteAvatarAction.rejected, (state, { error }) => {
      state.isUpdatingAvatar = false;
      console.error(error);
    });

    builder.addCase(deleteBackgroundAction.pending, (state) => {
      state.isUpdatingBackground = true;
    });
    builder.addCase(deleteBackgroundAction.fulfilled, (state, { payload }) => {
      state.isUpdatingBackground = false;
      state.myProfile.customBackground = "";
    });
    builder.addCase(deleteBackgroundAction.rejected, (state, { error }) => {
      state.isUpdatingBackground = false;
      console.error(error);
    });

  },
});

export const {
  setIsCreating,
  clearMyProfile,
  setIsUpdatingAvatar,
  setIsUpdatingBackground,
} = profileSlice.actions;

export default profileSlice.reducer;
